import type { CSSProperties } from 'react';

/**
 * RatingBadge — composant unifié pour TOUTES les surfaces d'affichage de
 * ratings utilisateur (T3 Salve P1 Stats Hub).
 *
 * Format standard : [Plateforme] [Cadence] [Valeur]
 *
 * Couleurs par plateforme :
 *  - FIDE  → paper-50 (blanc neutre, rating officiel)
 *  - Lichess → violet-500 (palette opérateur Opening Lab + brand Lichess)
 *  - chess.com → amber-500 (palette opérateur Endgame Forge + brand chess.com)
 *
 * Variants :
 *  - chip : FixedNav header, dense, 1 ligne
 *  - kpi  : Mission Control tile, valeur grosse
 *  - card : StatsPage onglets détaillés, complet avec timestamp
 *
 * Note : `<RatingDuo>` interne TacticsDrillPage reste local (cf. RATINGS_AUDIT §2.6).
 */

export type RatingPlatform = 'fide' | 'lichess' | 'chesscom';

const PLATFORM_COLOR: Record<RatingPlatform, string> = {
  fide: 'var(--paper-50)',
  lichess: 'var(--violet-500)',
  chesscom: 'var(--amber-500)',
};

const PLATFORM_LABEL: Record<RatingPlatform, string> = {
  fide: 'FIDE',
  lichess: 'Lichess',
  chesscom: 'chess.com',
};

const CADENCE_LABEL_FR: Record<string, string> = {
  // FIDE
  standard: 'Standard',
  rapide: 'Rapide',
  blitz: 'Blitz',
  // Lichess
  classical: 'Classique',
  rapid: 'Rapide',
  bullet: 'Bullet',
  puzzle: 'Puzzle',
  puzzleRush: 'Puzzle Rush',
  // chess.com
  tactics: 'Tactique',
  daily: 'Daily',
};

function cadenceFr(cadence: string): string {
  return CADENCE_LABEL_FR[cadence] ?? cadence;
}

export interface RatingBadgeProps {
  platform: RatingPlatform;
  cadence: string;
  value: number | null | undefined;
  updatedAt?: string | null;
  variant?: 'chip' | 'kpi' | 'card';
  target?: string;
}

export function RatingBadge({
  platform,
  cadence,
  value,
  updatedAt,
  variant = 'chip',
  target,
}: RatingBadgeProps): JSX.Element {
  const color = PLATFORM_COLOR[platform];
  const platformLabel = PLATFORM_LABEL[platform];
  const cadLabel = cadenceFr(cadence);
  const displayValue = value == null ? '—' : String(Math.round(value));

  if (variant === 'chip') {
    return (
      <div
        className="px-2 py-1 rounded-md border border-ink-700 bg-ink-800 font-mono text-[11px] text-paper-50 tabular-nums whitespace-nowrap"
        title={`${platformLabel} ${cadLabel}${updatedAt ? ` · ${updatedAt}` : ''}`}
      >
        <span className="text-ink-300 mr-1.5 uppercase tracking-wider text-[10px]">
          {platformLabel} {cadLabel}
        </span>
        <span style={{ color }}>{displayValue}</span>
      </div>
    );
  }

  if (variant === 'kpi') {
    return (
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-2 justify-between">
        <div>
          <span
            className="text-caption uppercase block"
            style={{ color, letterSpacing: '0.05em', fontWeight: 500 }}
          >
            {platformLabel} {cadLabel}
          </span>
          <div className="flex items-center justify-between gap-3 mt-1">
            <span
              className="font-mono tabular-nums"
              style={{ fontSize: 28, color: 'var(--paper-50)', lineHeight: 1, fontWeight: 500 }}
            >
              {displayValue}
            </span>
          </div>
          {target !== undefined && (
            <div className="flex items-center gap-1.5 text-[11px] font-mono whitespace-nowrap mt-1.5">
              <span className="text-ink-300">· cible {target}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // variant === 'card'
  const dotStyle: CSSProperties = { backgroundColor: color };
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full" style={dotStyle} aria-hidden="true" />
        <span
          className="text-caption uppercase"
          style={{ color, letterSpacing: '0.05em', fontWeight: 500 }}
        >
          {platformLabel} · {cadLabel}
        </span>
      </div>
      <div className="font-mono tabular-nums" style={{ fontSize: 32, color: 'var(--paper-50)' }}>
        {displayValue}
      </div>
      {updatedAt && (
        <div className="text-[11px] font-mono text-ink-300">maj {updatedAt}</div>
      )}
    </div>
  );
}
